import { AttributablePay } from '@fora-monorepo/employer-my-pages-common/types';
import { validateRules, ValidationResult, ValidationRule } from '@foradevelopment/fora-ui';
import { removeWhitespaces, stringToNumberOrNull } from '@fora-monorepo/string-utils';

import { isValidPayAmount } from './isValidPayAmount';
import { isPayLargerThanAttributableTotal } from '../../common/utils/validation/helpers/isPayLargerThanAttributableTotal';
import { typedT } from '../../i18n/i18n';

/** The first error in order will be the one shown in the GUI */
export const getInputValidator =
  ({
    attributablePay,
    fokIsExpiring,
    isPeriodOpen,
    personDeceased,
    reportedPayAmount,
    isAbovePensionIncomeAge,
  }: {
    attributablePay: AttributablePay | undefined;
    fokIsExpiring: string | null | undefined;
    isPeriodOpen: boolean | null;
    personDeceased: boolean;
    reportedPayAmount: number | null;
    isAbovePensionIncomeAge: boolean;
  }) =>
  (value: string) => {
    const validators = [
      // It's important to have this first in order
      isFokValid(typedT('errorMessages.validation.payroll.fokIsExpiring', { fokIsExpiring }), fokIsExpiring),
      isValidPay(typedT('errorMessages.validation.payroll.enterAmount')),
    ];
    validators.push(
      isNotDecreasingPayForDeceasedIndividual(
        typedT('errorMessages.validation.payroll.deceasedIndividual'),
        isPeriodOpen,
        reportedPayAmount,
        personDeceased
      ),
      isNotDecreasingPayForPensionAgedIndividual(
        typedT('errorMessages.validation.payroll.pensionAgedIndividual'),
        isPeriodOpen,
        reportedPayAmount,
        isAbovePensionIncomeAge
      ),
      isPayLargerThanAttributableTotal(
        typedT('errorMessages.validation.payroll.attributablePayIsLowerThanPayroll'),
        attributablePay
      )
    );

    return validateRules(validators, value);
  };

const isValidPay =
  (errorMessage: string): ValidationRule =>
  (inputToValidate: string): ValidationResult => {
    if (!isValidPayAmount(removeWhitespaces(inputToValidate))) {
      return { error: true, errorMessage };
    }
    return { error: false, errorMessage };
  };

const isFokValid =
  (errorMessage: string, fokIsExpiring?: string | null): ValidationRule =>
  (_: string): ValidationResult => {
    if (fokIsExpiring) {
      return { error: true, errorMessage };
    }
    return { error: false, errorMessage };
  };

const isNotDecreasingPayForDeceasedIndividual =
  (
    errorMessage: string,
    isPeriodOpen: boolean | null,
    reportedPayAmount: number | null,
    personDeceased: boolean
  ): ValidationRule =>
  (inputToValidate: string): ValidationResult => {
    if (personDeceased && !isPeriodOpen && reportedPayAmount !== null) {
      const amount = stringToNumberOrNull(inputToValidate);
      if (amount === null || amount < reportedPayAmount) return { error: true, errorMessage };
    }
    return { error: false, errorMessage };
  };

const isNotDecreasingPayForPensionAgedIndividual =
  (
    errorMessage: string,
    isPeriodOpen: boolean | null,
    reportedPayAmount: number | null,
    isAbovePensionIncomeAge: boolean
  ): ValidationRule =>
  (inputToValidate: string): ValidationResult => {
    if (isAbovePensionIncomeAge && !isPeriodOpen && reportedPayAmount !== null) {
      const amount = stringToNumberOrNull(inputToValidate);
      if (amount === null || amount < reportedPayAmount) return { error: true, errorMessage };
    }
    return { error: false, errorMessage };
  };
